import React, { useContext } from 'react';

import { MdPlaylistAddCheckCircle, MdPlaylistAddCircle } from 'react-icons/md';

import { Link } from 'react-router-dom';
import { useIntl } from 'react-intl';

import { Tour, Concert } from 'interfaces';

import { AppLocale, BAND_NAME, BAND_NAME_JP } from 'constant';

import { LanguageContext } from 'context/languageContext';

import Title from 'components/ui/Title';

import {
  Wrapper,
  List,
  ListItem,
  NumberWrapper,
  DateWrapper,
  ConcertName,
  Name,
  TourDetailsList,
  TourDetailsListItem,
  TourAreaWrapper,
  TourArea,
  TourDescription,
  Icon
} from './TourDetails.styles';
import { getAlbum } from '../../utils';

export interface TourDetailsProps {
  tour?: Tour;
  concerts: Concert[];
}

const TourDetails = ({ tour, concerts }: TourDetailsProps) => {
  const { locale } = useContext(LanguageContext);
  const intl = useIntl();

  return (
    <Wrapper>
      {
        tour ? (
          <React.Fragment>
            <Title size="big" color="secondary">
              {locale === AppLocale.EN ? tour.englishName || tour.name : tour.name}
            </Title>
            <TourDetailsList>
              {
                tour.album ? (
                  <TourDetailsListItem>
                    {intl.formatMessage({ id: 'tours.album' }, { album: (
                      <Link to={getAlbum(tour.album, false, locale, 'link')}>{getAlbum(tour.album, false, locale, 'name')}</Link>)
                    })}
                  </TourDetailsListItem>
                ) : null
              }

              <TourDetailsListItem>
                {intl.formatMessage({ id: 'tours.duration' })}
                {intl.formatDateTimeRange(new Date(tour.dateStart), new Date(tour.dateEnd), {
                  year: "numeric",
                  month: "short",
                  day: "numeric"
                })}
                {intl.formatMessage({ id: 'tours.numberOfConcerts' }, { number: tour.shows })}
              </TourDetailsListItem>
              <TourDetailsListItem>
                {intl.formatMessage({ id: 'tours.region' })}
                <TourAreaWrapper>
                  {
                    tour.area.map((area) => (
                      <TourArea area={area} key={area}>
                        {intl.formatMessage({ id: `tours.area.${area}` })}
                      </TourArea>
                    ))
                  }
                </TourAreaWrapper>
              </TourDetailsListItem>
            </TourDetailsList>
            {
              tour.description.length ? (
                <TourDescription>
                  <Title>{intl.formatMessage({ id: 'tours.descriptions.title' })}</Title>
                  {
                    tour.description.map((desc, index) => (
                      <p key={index}>
                        {intl.formatMessage({ id: `tours.descriptions.${tour.id}.${desc}` }, {
                          bandName: locale === AppLocale.EN ? BAND_NAME : BAND_NAME_JP,
                          tourName: tour.name
                        })}
                      </p>
                    ))
                  }
                </TourDescription>
              ) : null
            }
          </React.Fragment>
        ) : null
      }
      <Title color="secondary">{intl.formatMessage({ id: 'tours.concertList.title' })}</Title>
      <List>
        {
          concerts.map((concert, index) => (
            <ListItem key={concert.id}>
              <ConcertName>
                <NumberWrapper>{index + 1}.</NumberWrapper>
                <Link
                  to={
                  concert.tourId !== 'festivals-shows-special-events'
                      ? `/tours-and-shows/${concert.tourId}/${concert.id}`
                      : `/tours-and-shows/shows/festivals-shows-special-events/${concert.id}`
                  }
                >
                  <DateWrapper>
                    {intl.formatDate(new Date(concert.date), {
                      year: 'numeric', month: 'short', day: 'numeric'
                    })}
                  </DateWrapper>
                  <Name>
                    {intl.formatMessage({ id: 'concert.name' }, {
                      bandName: locale === AppLocale.EN ? BAND_NAME : BAND_NAME_JP,
                      name: concert.name ? `${intl.formatMessage({ id: `concert.name.${concert.name}` })}${locale === AppLocale.EN ? ', ' : '、'}` : '',
                      venue: intl.formatMessage({ id: `concert.venue.${concert.venueId}` }),
                      city: intl.formatMessage({ id: `concert.city.${concert.cityId}` }),
                      country: intl.formatMessage({ id: `concert.country.${concert.countryId}` })
                    })}
                  </Name>
                </Link>
                <Icon hasSongList={concert.setListConfirmed && concert.songList.length !== 0}>
                  {
                    concert.songList.length ? (
                      <MdPlaylistAddCheckCircle title={intl.formatMessage({ id: 'concert.setList.added' })} />
                    ) : <MdPlaylistAddCircle title={intl.formatMessage({ id: 'concert.setList.missing' })} />
                  }
                </Icon>
              </ConcertName>
            </ListItem>
          ))
        }

      </List>
    </Wrapper>
  );
};

export default TourDetails;
